import { formatPublicCycleUrl } from "@/lib/api/publicCycle";
import {
  generateRsvpSalt,
  hashRsvpPassword,
} from "@/lib/api/publicRsvp";

export function formatPublicPollUrl(token: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/poll/public/${token}`;
}

export interface PublicPollSlot {
  id: string;
  slot_time: string;
  vote_count: number;
}

export interface PublicPollDay {
  id: string;
  day: string;
  position: number;
  slots: PublicPollSlot[];
}

export interface PublicPollInfo {
  id: string;
  group_id: string;
  requires_password: boolean;
  question: string | null;
  mode: string | null;
  closes_at: string | null;
  days: PublicPollDay[];
}

type PublicPollErrorCode =
  | "MISSING_FIELDS"
  | "POLL_NOT_FOUND"
  | "PASSWORD_NOT_SET"
  | "INVALID_PASSWORD"
  | "POLL_CLOSED"
  | "INVALID_PLAYER"
  | "INTERNAL_ERROR"
  | "PUBLIC_POLL_FAILED";

async function callPublicPollApi(
  body: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const res = await fetch(`${supabaseUrl}/functions/v1/public-poll`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify(body),
  });

  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((payload as { error?: PublicPollErrorCode }).error ?? "PUBLIC_POLL_FAILED");
  }
  return payload as Record<string, unknown>;
}

function mapPollFromPayload(payload: Record<string, unknown>): PublicPollInfo | null {
  const poll = payload.poll as Record<string, unknown> | undefined;
  if (!poll?.id) return null;
  return {
    id: poll.id as string,
    group_id: poll.group_id as string,
    requires_password: Boolean(poll.poll_password_hash),
    question: (poll.question as string) ?? null,
    mode: (poll.mode as string) ?? null,
    closes_at: (poll.closes_at as string) ?? null,
    days: (poll.days as PublicPollDay[]) ?? [],
  };
}

export async function fetchPublicPoll(token: string): Promise<PublicPollInfo | null> {
  if (!token) return null;
  const { supabase } = await import("@/lib/supabase");
  const { data, error } = await supabase
    .from("polls")
    .select("id, group_id, poll_password_hash, question, mode, closes_at")
    .eq("poll_public_token", token)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const { data: days, error: daysError } = await supabase
    .from("poll_days")
    .select(`
      id,
      day,
      position,
      slots:poll_slots(
        id,
        slot_time,
        votes:poll_slot_votes(id)
      )
    `)
    .eq("poll_id", data.id)
    .order("position");

  if (daysError) throw daysError;

  const mappedDays: PublicPollDay[] = (days ?? []).map((day) => ({
    id: day.id,
    day: day.day,
    position: day.position,
    slots: (day.slots ?? []).map((slot: { id: string; slot_time: string; votes?: { id: string }[] }) => ({
      id: slot.id,
      slot_time: slot.slot_time,
      vote_count: slot.votes?.length ?? 0,
    })),
  }));

  return {
    id: data.id,
    group_id: data.group_id,
    requires_password: Boolean(data.poll_password_hash),
    question: data.question,
    mode: data.mode,
    closes_at: data.closes_at,
    days: mappedDays,
  };
}

export async function fetchPublicPollVotedPlayerIds(pollId: string): Promise<number[]> {
  const { supabase } = await import("@/lib/supabase");
  const { data, error } = await supabase
    .from("poll_slot_votes")
    .select("player_id")
    .eq("poll_id", pollId)
    .not("player_id", "is", null);

  if (error) throw error;
  return (data ?? [])
    .map((row) => row.player_id as number | null)
    .filter((id): id is number => id != null);
}

export async function setupPublicPollPassword(
  pollId: string,
  password: string
): Promise<{ token: string; url: string }> {
  const { supabase } = await import("@/lib/supabase");
  const salt = generateRsvpSalt();
  const hash = `${salt}:${await hashRsvpPassword(password, salt)}`;

  const { data, error } = await supabase
    .from("polls")
    .update({ poll_password_hash: hash })
    .eq("id", pollId)
    .select("poll_public_token")
    .single();

  if (error) throw error;
  const token = data.poll_public_token as string;
  return { token, url: formatPublicCycleUrl(token) };
}

export async function verifyPublicPollPassword(token: string, password: string): Promise<PublicPollInfo | null> {
  const payload = await callPublicPollApi({ action: "verify", token, password });
  return mapPollFromPayload(payload);
}

export async function joinPublicPollGuest(payload: {
  token: string;
  password: string;
  guestName: string;
}): Promise<number> {
  const result = await callPublicPollApi({
    action: "join_guest",
    token: payload.token,
    password: payload.password,
    guestName: payload.guestName,
  });
  return result.playerId as number;
}

export async function submitPublicPollVotes(payload: {
  token: string;
  password: string;
  slotIds: string[];
  playerId: number;
}): Promise<PublicPollInfo | null> {
  const result = await callPublicPollApi({
    action: "submit",
    token: payload.token,
    password: payload.password,
    slotIds: payload.slotIds,
    playerId: payload.playerId,
  });
  return mapPollFromPayload(result);
}

/** @deprecated legacy single-option vote */
export async function submitPublicPollVote(payload: {
  token: string;
  password: string;
  pollOptionId: string;
  playerId: number;
}): Promise<void> {
  await callPublicPollApi({
    action: "submit",
    token: payload.token,
    password: payload.password,
    slotIds: [payload.pollOptionId],
    playerId: payload.playerId,
  });
}
